import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { parseDocumentPageMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { parseSystemDocumentPageEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { bareDocumentPageId } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import {
  documentPageMetadataUserEditsBySite,
  systemDocumentPagesByPageIds,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import type { DocumentRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/documentRevisionFamily'
import { latestConfirmedPageUserEditForPage } from 'features/knowledgeDocumentViewer/utils/selectLatestConfirmedPageMetadataUserEdit'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'

export type UseDocumentRevisionFamilyCascadeTargetsArgs = {
  members: readonly DocumentRevisionFamilyMember[]
  cascadePageNum?: number
  enabled?: boolean
}

export type UseDocumentRevisionFamilyCascadeTargetsResult = {
  pagesByBqDocumentId: ReadonlyMap<string, SystemDocumentPageEntry>
  priorPageEditsByBqDocumentId: ReadonlyMap<
    string,
    DocumentPageMetadataUserEditEntry
  >
  mutateCascadePriorEdits: () => Promise<unknown>
  areCascadePriorEditsReady: boolean
  isCascadeLoading: boolean
}

/**
 * Loads sibling system pages + page prior-edits used when cascading document
 * family saves onto Page Details. Kept separate from revision-family discovery.
 */
export function useDocumentRevisionFamilyCascadeTargets({
  members,
  cascadePageNum,
  enabled = true,
}: UseDocumentRevisionFamilyCascadeTargetsArgs): UseDocumentRevisionFamilyCascadeTargetsResult {
  const { siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const cascadePageLoadOptions = useMemo(() => {
    if (
      !enabled ||
      isPipelineConfigScopeLoading ||
      cascadePageNum === undefined ||
      cascadePageNum < 1 ||
      members.length === 0
    ) {
      return undefined
    }
    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined
    const pageIds = members.map((member) =>
      bareDocumentPageId(member.bqDocumentId, cascadePageNum)
    )
    return systemDocumentPagesByPageIds(pageIds, scopedTags)
  }, [
    cascadePageNum,
    enabled,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    members,
    siteID,
  ])

  const { data: rawCascadePages, isLoading: isLoadingCascadePages } =
    useGetRolodexEntriesByLoadOptions<unknown>(cascadePageLoadOptions)

  const pagesByBqDocumentId = useMemo(() => {
    const map = new Map<string, SystemDocumentPageEntry>()
    if (rawCascadePages === undefined || cascadePageNum === undefined) {
      return map
    }
    const pages = parseSystemDocumentPageEntries(rawCascadePages)
    for (const page of pages) {
      const bqDocumentId = page.entry.data.document_id?.trim()
      if (!bqDocumentId) continue
      if (page.entry.data.page_num !== cascadePageNum) continue
      map.set(bqDocumentId, page)
    }
    return map
  }, [cascadePageNum, rawCascadePages])

  const pagePriorEditLoadOptions = useMemo(() => {
    if (
      !enabled ||
      !siteID ||
      cascadePageNum === undefined ||
      members.length === 0
    ) {
      return undefined
    }
    return documentPageMetadataUserEditsBySite({ site: siteID })
  }, [cascadePageNum, enabled, members, siteID])

  const {
    data: rawPagePriorEdits,
    isLoading: isLoadingPagePriorEdits,
    mutate: mutateCascadePriorEdits,
  } = useGetRolodexEntriesByLoadOptions<unknown>(pagePriorEditLoadOptions)

  const priorPageEditsByBqDocumentId = useMemo(() => {
    const map = new Map<string, DocumentPageMetadataUserEditEntry>()
    if (
      rawPagePriorEdits === undefined ||
      !siteID ||
      cascadePageNum === undefined
    ) {
      return map
    }
    const edits = parseDocumentPageMetadataUserEditEntries(rawPagePriorEdits)
    for (const member of members) {
      const edit = latestConfirmedPageUserEditForPage(
        edits,
        member.bqDocumentId,
        cascadePageNum
      )
      if (edit) map.set(member.bqDocumentId, edit)
    }
    return map
  }, [cascadePageNum, members, rawPagePriorEdits, siteID])

  const isCascadeLoading =
    (cascadePageLoadOptions !== undefined && isLoadingCascadePages) ||
    (pagePriorEditLoadOptions !== undefined && isLoadingPagePriorEdits)

  const areCascadePriorEditsReady =
    (pagePriorEditLoadOptions === undefined ||
      rawPagePriorEdits !== undefined) &&
    (cascadePageLoadOptions === undefined || rawCascadePages !== undefined)

  return {
    pagesByBqDocumentId,
    priorPageEditsByBqDocumentId,
    mutateCascadePriorEdits,
    areCascadePriorEditsReady,
    isCascadeLoading,
  }
}
